import {
  Button,
  Checkbox,
  DataTable,
  Modal,
  Pagination,
  Popover,
  Scrollable,
  Spinner,
  Stack,
  Tag,
  TextField,
} from "@shopify/polaris";
import React, { useCallback, useEffect, useState } from "react";

const CsvPreviewModal = ({ open, onClose, importId, shopId }) => {
  const [data, setData] = useState({
    headers: [],
    rows: [],
    total: 0,
    total_pages: 0,
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);

  // Initialize visible columns when data loads
  useEffect(() => {
    if (data.headers.length > 0) {
      // Default to showing all columns initially
      setVisibleColumns(data.headers);
    }
  }, [data.headers]);

  const toggleColumnSelector = useCallback(
    () => setColumnSelectorOpen((active) => !active),
    [],
  );

  const handleColumnToggle = useCallback((column) => {
    setVisibleColumns((current) => {
      if (current.includes(column)) {
        return current.filter((c) => c !== column);
      } else {
        return [...current, column];
      }
    });
  }, []);

  const handleSelectAllColumns = useCallback(() => {
    setVisibleColumns(data.headers);
  }, [data.headers]);

  const handleDeselectAllColumns = useCallback(() => {
    setVisibleColumns([]);
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPreview = useCallback(async () => {
    if (!importId || !shopId) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        search: debouncedSearch,
        page: page.toString(),
        per_page: "50",
      });

      const headers = {
        "Content-Type": "application/json",
      };

      if (localStorage.auth_token) {
        headers["Authorization"] = `Bearer ${localStorage.auth_token}`;
      }

      const response = await fetch(
        `/api/shops/${shopId}/pos_import_histories/${importId}/preview?${params}`,
        { headers },
      );

      if (!response.ok) {
        const result = await response.json();
        setError(result.error || `Failed to load preview (${response.status})`);
        setData({ headers: [], rows: [], total: 0, total_pages: 0 });
        return;
      }

      const result = await response.json();

      if (result.error) {
        setError(result.error);
        setData({ headers: [], rows: [], total: 0, total_pages: 0 });
      } else {
        setData(result);
      }
    } catch (err) {
      console.error("Preview fetch failed:", err);
      setError(
        "Failed to load file preview. The file may no longer be available.",
      );
    } finally {
      setLoading(false);
    }
  }, [importId, shopId, debouncedSearch, page]);

  useEffect(() => {
    if (open && importId) {
      fetchPreview();
    }
  }, [open, importId, fetchPreview]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearch("");
      setPage(1);
      setData({ headers: [], rows: [], total: 0, total_pages: 0 });
      setError(null);
    }
  }, [open]);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch("");
  }, []);

  // Prepare table rows
  const displayHeaders = data.headers.filter((h) => visibleColumns.includes(h));
  const hasHiddenColumns = data.headers.length > displayHeaders.length;

  const tableRows = data.rows.map((row) =>
    displayHeaders.map((header) => {
      const value = row[header] || "";
      // Truncate long values
      return value.length > 50 ? value.substring(0, 47) + "..." : value;
    }),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`CSV Preview${
        data.total > 0 ? ` (${data.total.toLocaleString()} rows)` : ""
      }`}
      large
    >
      {/* Custom style to make this specific modal wider */}
      <style>{`
        /* Target multiple potential classes for Polaris Modal */
        .Polaris-Modal-Dialog__Modal,
        .Polaris-Modal-Dialog {
          max-width: 95vw !important;
          width: 1200px !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }
        
        /* Ensure the container allows growth and centering */
        .Polaris-Modal-Dialog__Container {
          width: 100% !important;
          display: flex !important;
          justify-content: center !important;
        }

        @media (max-width: 1200px) {
          .Polaris-Modal-Dialog__Modal,
          .Polaris-Modal-Dialog {
            width: 95vw !important;
          }
        }
      `}</style>
      <Modal.Section>
        <div style={{ marginBottom: "16px", display: "flex", gap: "12px" }}>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Search all columns..."
              value={search}
              onChange={handleSearchChange}
              autoComplete="off"
              clearButton
              onClearButtonClick={handleClearSearch}
              prefix="🔍"
            />
          </div>
          <Popover
            active={columnSelectorOpen}
            activator={
              <Button onClick={toggleColumnSelector} disclosure>
                Columns{" "}
                {hasHiddenColumns
                  ? `(${displayHeaders.length}/${data.headers.length})`
                  : ""}
              </Button>
            }
            onClose={toggleColumnSelector}
          >
            <div style={{ padding: "12px", minWidth: "250px" }}>
              <Stack vertical spacing="tight">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    paddingBottom: "8px",
                    borderBottom: "1px solid #dfe3e8",
                  }}
                >
                  <Button plain onClick={handleSelectAllColumns}>
                    Show All
                  </Button>
                  <Button plain onClick={handleDeselectAllColumns}>
                    Hide All
                  </Button>
                </div>
                <Scrollable shadow style={{ maxHeight: "300px" }}>
                  <Stack vertical spacing="extraTight">
                    {data.headers.map((header) => (
                      <Checkbox
                        key={header}
                        label={header}
                        checked={visibleColumns.includes(header)}
                        onChange={() => handleColumnToggle(header)}
                      />
                    ))}
                  </Stack>
                </Scrollable>
              </Stack>
            </div>
          </Popover>
        </div>

        {error && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#fef2f2",
              borderRadius: "8px",
              color: "#dc2626",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "48px" }}>
            <Spinner size="large" />
            <p style={{ marginTop: "16px", color: "#637381" }}>
              Loading preview...
            </p>
          </div>
        ) : data.rows.length > 0 ? (
          <>
            {hasHiddenColumns && (
              <div style={{ marginBottom: "12px" }}>
                <Tag onRemove={handleSelectAllColumns}>
                  {data.headers.length - displayHeaders.length} columns hidden
                </Tag>
              </div>
            )}

            <div style={{ overflowX: "auto", maxWidth: "100%" }}>
              <DataTable
                columnContentTypes={displayHeaders.map(() => "text")}
                headings={displayHeaders}
                rows={tableRows}
                truncate
                verticalAlign="middle"
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <span style={{ color: "#6b7280", fontSize: "0.9em" }}>
                Page {page} of {data.total_pages || 1}
                {debouncedSearch && ` (filtered from ${data.total} results)`}
              </span>
              <Pagination
                hasPrevious={page > 1}
                hasNext={page < data.total_pages}
                onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => p + 1)}
              />
            </div>
          </>
        ) : !loading && !error ? (
          <div
            style={{
              textAlign: "center",
              padding: "48px",
              color: "#6b7280",
            }}
          >
            {debouncedSearch
              ? "No rows match your search"
              : "No data available in this file"}
          </div>
        ) : null}
      </Modal.Section>

      <Modal.Section>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Close</Button>
        </div>
      </Modal.Section>
    </Modal>
  );
};

export default CsvPreviewModal;
